/**
 * Transaction process graph for product orders:
 *   - default-purchase
 */

/**
 * Transitions
 *
 * These strings must sync with values defined in Marketplace API,
 * since transaction objects given by API contain info about last transitions.
 * All the actions in API side happen in transitions,
 * so we need to understand what those strings mean.
 */

export const transitions = {
  // When a customer makes an order for a listing, a transaction is
  // created with the initial request-payment transition.
  // At this transition a PaymentIntent is created by Marketplace API.
  // After this transition, the actual payment must be made on client-side directly to Stripe.
  REQUEST_PAYMENT: 'transition/request-payment',

  // A customer can also initiate a transaction with an inquiry, and
  // then transition that with a request.
  INQUIRE: 'transition/inquire',
  REQUEST_PAYMENT_AFTER_INQUIRY: 'transition/request-payment-after-inquiry',

  // Nextmine: MAKE AN OFFER - køber byder et beløb (ingen betaling endnu).
  // Sælger accepterer/afslår; accepteres buddet, betaler køber → samme flow.
  MAKE_OFFER: 'transition/make-offer',
  PROVIDER_ACCEPT_OFFER: 'transition/provider-accept-offer',
  PROVIDER_DECLINE_OFFER: 'transition/provider-decline-offer',
  CUSTOMER_WITHDRAW_OFFER: 'transition/customer-withdraw-offer',
  EXPIRE_OFFER: 'transition/expire-offer',
  // Modbud (counter-offer) - sælger svarer med en pris; køber afgør (kan byde igen)
  PROVIDER_MAKE_COUNTER_OFFER: 'transition/provider-make-counter-offer',
  CUSTOMER_ACCEPT_COUNTER: 'transition/customer-accept-counter',
  CUSTOMER_DECLINE_COUNTER: 'transition/customer-decline-counter',
  CUSTOMER_MAKE_COUNTER_OFFER: 'transition/customer-make-counter-offer',
  EXPIRE_COUNTER: 'transition/expire-counter',
  REQUEST_PAYMENT_AFTER_OFFER: 'transition/request-payment-after-offer',
  EXPIRE_OFFER_ACCEPTED: 'transition/expire-offer-accepted',

  // Nextmine: REQUEST INTERNATIONAL SHIPPING QUOTE - international køber beder
  // om en fragtpris. Sælger svarer; køber betaler vare + aftalt fragt.
  REQUEST_SHIPPING_QUOTE: 'transition/request-shipping-quote',
  PROVIDER_PROVIDE_SHIPPING_QUOTE: 'transition/provider-provide-shipping-quote',
  PROVIDER_DECLINE_SHIPPING_REQUEST: 'transition/provider-decline-shipping-request',
  CUSTOMER_WITHDRAW_SHIPPING_REQUEST: 'transition/customer-withdraw-shipping-request',
  EXPIRE_SHIPPING_REQUEST: 'transition/expire-shipping-request',
  CUSTOMER_DECLINE_SHIPPING_QUOTE: 'transition/customer-decline-shipping-quote',
  EXPIRE_SHIPPING_QUOTE: 'transition/expire-shipping-quote',
  REQUEST_PAYMENT_AFTER_SHIPPING_QUOTE: 'transition/request-payment-after-shipping-quote',
  // Nextmine: international køber byder på varen EFTER fragt-quoten
  MAKE_OFFER_AFTER_SHIPPING_QUOTE: 'transition/make-offer-after-shipping-quote',

  // Stripe SDK might need to ask 3D security from customer, in a separate front-end step.
  // Therefore we need to make another transition to Marketplace API,
  // to tell that the payment is confirmed.
  CONFIRM_PAYMENT: 'transition/confirm-payment',

  // If the payment is not confirmed in the time limit set in transaction process (by default 15min)
  // the transaction will expire automatically.
  EXPIRE_PAYMENT: 'transition/expire-payment',

  // Provider or opeartor can mark the product shipped/delivered
  MARK_DELIVERED: 'transition/mark-delivered',
  OPERATOR_MARK_DELIVERED: 'transition/operator-mark-delivered',

  // Customer can mark the product received (e.g. picked up from provider)
  MARK_RECEIVED_FROM_PURCHASED: 'transition/mark-received-from-purchased',

  // Automatic cancellation happens if none marks the delivery happened
  AUTO_CANCEL: 'transition/auto-cancel',

  // Operator can cancel the purchase before product has been marked as delivered / received
  CANCEL: 'transition/cancel',

  // If provider has marked the product delivered (e.g. shipped),
  // customer can then mark the product received
  MARK_RECEIVED: 'transition/mark-received',

  // If customer doesn't mark the product received manually, it can happen automatically
  AUTO_MARK_RECEIVED: 'transition/auto-mark-received',

  // When provider has marked the product delivered, customer or operator can dispute the transaction
  DISPUTE: 'transition/dispute',
  OPERATOR_DISPUTE: 'transition/operator-dispute',

  // If nothing is done to disputed transaction it ends up to Canceled state
  AUTO_CANCEL_FROM_DISPUTED: 'transition/auto-cancel-from-disputed',

  // Operator can cancel disputed transaction manually
  CANCEL_FROM_DISPUTED: 'transition/cancel-from-disputed',

  // Operator can mark the disputed transaction as received
  MARK_RECEIVED_FROM_DISPUTED: 'transition/mark-received-from-disputed',

  // Nextmine: RETUR-FLOW - tvist løst mod at køber returnerer varen (køber
  // betaler returporto). Pengene er aldrig udbetalt; refunderes når returen
  // bekræftes modtaget.
  REQUEST_RETURN: 'transition/request-return',
  SUBMIT_RETURN_TRACKING: 'transition/submit-return-tracking',
  CONFIRM_RETURN_RECEIVED: 'transition/confirm-return-received',
  OPERATOR_CONFIRM_RETURN: 'transition/operator-confirm-return',
  RETURN_NOT_AS_EXPECTED: 'transition/return-not-as-expected',
  AUTO_REFUND_RETURN: 'transition/auto-refund-return',
  EXPIRE_RETURN_REQUEST: 'transition/expire-return-request',

  // System moves transaction automatically from received state to complete state
  // This makes it possible to to add notifications to that single transition.
  AUTO_COMPLETE: 'transition/auto-complete',

  // Nextmine: operatør kan stoppe udbetalingen og refundere i 14-dages-holdet
  OPERATOR_REFUND_DURING_HOLD: 'transition/operator-refund-during-hold',

  // Reviews are given through transaction transitions. Review 1 can be
  // by provider or customer, and review 2 will be the other party of
  // the transaction.
  REVIEW_1_BY_PROVIDER: 'transition/review-1-by-provider',
  REVIEW_2_BY_PROVIDER: 'transition/review-2-by-provider',
  REVIEW_1_BY_CUSTOMER: 'transition/review-1-by-customer',
  REVIEW_2_BY_CUSTOMER: 'transition/review-2-by-customer',
  EXPIRE_CUSTOMER_REVIEW_PERIOD: 'transition/expire-customer-review-period',
  EXPIRE_PROVIDER_REVIEW_PERIOD: 'transition/expire-provider-review-period',
  EXPIRE_REVIEW_PERIOD: 'transition/expire-review-period',
};

/**
 * States
 *
 * These constants are only for making it clear how transitions work together.
 * You should not use these constants outside of this file.
 *
 * Note: these states are not in sync with states used transaction process definitions
 *       in Marketplace API. Only last transitions are passed along transaction object.
 */

export const states = {
  INITIAL: 'initial',
  INQUIRY: 'inquiry',
  // Nextmine: bud-tilstande (Make an offer + modbud)
  OFFER_PENDING: 'offer-pending',
  COUNTER_PENDING: 'counter-pending',
  OFFER_ACCEPTED: 'offer-accepted',
  OFFER_REJECTED: 'offer-rejected',
  // Nextmine: international fragt-quote-tilstande
  SHIPPING_QUOTE_REQUESTED: 'shipping-quote-requested',
  SHIPPING_QUOTE_PROVIDED: 'shipping-quote-provided',
  SHIPPING_QUOTE_REJECTED: 'shipping-quote-rejected',
  PENDING_PAYMENT: 'pending-payment',
  PAYMENT_EXPIRED: 'payment-expired',
  PURCHASED: 'purchased',
  DELIVERED: 'delivered',
  RECEIVED: 'received',
  DISPUTED: 'disputed',
  // Nextmine: retur-tilstande
  RETURN_REQUESTED: 'return-requested',
  RETURN_SHIPPED: 'return-shipped',
  CANCELED: 'canceled',
  COMPLETED: 'completed',
  REVIEWED: 'reviewed',
  REVIEWED_BY_CUSTOMER: 'reviewed-by-customer',
  REVIEWED_BY_PROVIDER: 'reviewed-by-provider',
};

/**
 * Description of transaction process graph
 *
 * You should keep this in sync with transaction process defined in Marketplace API
 *
 * Note: we don't use yet any state machine library,
 *       but this description format is following Xstate (FSM library)
 *       https://xstate.js.org/docs/
 */
export const graph = {
  // id is defined only to support Xstate format.
  // However if you have multiple transaction processes defined,
  // it is best to keep them in sync with transaction process aliases.
  id: 'default-purchase/release-1',

  // This 'initial' state is a starting point for new transaction
  initial: states.INITIAL,

  // States
  states: {
    [states.INITIAL]: {
      on: {
        [transitions.INQUIRE]: states.INQUIRY,
        [transitions.REQUEST_PAYMENT]: states.PENDING_PAYMENT,
        // Nextmine: køber kan byde i stedet for at købe straks
        [transitions.MAKE_OFFER]: states.OFFER_PENDING,
        // Nextmine: international køber beder om fragt-quote
        [transitions.REQUEST_SHIPPING_QUOTE]: states.SHIPPING_QUOTE_REQUESTED,
      },
    },
    [states.INQUIRY]: {
      on: {
        [transitions.REQUEST_PAYMENT_AFTER_INQUIRY]: states.PENDING_PAYMENT,
      },
    },

    // Nextmine: bud afventer sælgers svar (accepter / afslå / modbud)
    [states.OFFER_PENDING]: {
      on: {
        // Nextmine: ingen decline - sælger kan kun acceptere eller modbyde
        [transitions.PROVIDER_ACCEPT_OFFER]: states.OFFER_ACCEPTED,
        [transitions.CUSTOMER_WITHDRAW_OFFER]: states.OFFER_REJECTED,
        [transitions.EXPIRE_OFFER]: states.OFFER_REJECTED,
        [transitions.PROVIDER_MAKE_COUNTER_OFFER]: states.COUNTER_PENDING,
      },
    },
    // Nextmine: sælgers modbud afventer købers svar (accepter / byd igen)
    [states.COUNTER_PENDING]: {
      on: {
        // Nextmine: ingen decline - køber kan kun acceptere eller byde igen
        [transitions.CUSTOMER_ACCEPT_COUNTER]: states.OFFER_ACCEPTED,
        [transitions.CUSTOMER_MAKE_COUNTER_OFFER]: states.OFFER_PENDING,
        [transitions.EXPIRE_COUNTER]: states.OFFER_REJECTED,
      },
    },
    // Nextmine: bud accepteret → køber betaler det aftalte beløb
    [states.OFFER_ACCEPTED]: {
      on: {
        [transitions.REQUEST_PAYMENT_AFTER_OFFER]: states.PENDING_PAYMENT,
        [transitions.EXPIRE_OFFER_ACCEPTED]: states.OFFER_REJECTED,
      },
    },
    [states.OFFER_REJECTED]: {},

    // Nextmine: international fragt-forespørgsel afventer sælgers quote
    [states.SHIPPING_QUOTE_REQUESTED]: {
      on: {
        [transitions.PROVIDER_PROVIDE_SHIPPING_QUOTE]: states.SHIPPING_QUOTE_PROVIDED,
        [transitions.PROVIDER_DECLINE_SHIPPING_REQUEST]: states.SHIPPING_QUOTE_REJECTED,
        [transitions.CUSTOMER_WITHDRAW_SHIPPING_REQUEST]: states.SHIPPING_QUOTE_REJECTED,
        [transitions.EXPIRE_SHIPPING_REQUEST]: states.SHIPPING_QUOTE_REJECTED,
      },
    },
    // Nextmine: sælgers fragt-quote afventer købers valg (betal / byd / afslå)
    [states.SHIPPING_QUOTE_PROVIDED]: {
      on: {
        [transitions.REQUEST_PAYMENT_AFTER_SHIPPING_QUOTE]: states.PENDING_PAYMENT,
        // Nextmine: køber kan også byde på varen efter fragt-quoten (fragt hænger ved)
        [transitions.MAKE_OFFER_AFTER_SHIPPING_QUOTE]: states.OFFER_PENDING,
        [transitions.CUSTOMER_DECLINE_SHIPPING_QUOTE]: states.SHIPPING_QUOTE_REJECTED,
        [transitions.EXPIRE_SHIPPING_QUOTE]: states.SHIPPING_QUOTE_REJECTED,
      },
    },
    [states.SHIPPING_QUOTE_REJECTED]: {},

    [states.PENDING_PAYMENT]: {
      on: {
        [transitions.EXPIRE_PAYMENT]: states.PAYMENT_EXPIRED,
        [transitions.CONFIRM_PAYMENT]: states.PURCHASED,
      },
    },

    [states.PAYMENT_EXPIRED]: {},
    [states.PURCHASED]: {
      on: {
        [transitions.MARK_DELIVERED]: states.DELIVERED,
        [transitions.OPERATOR_MARK_DELIVERED]: states.DELIVERED,
        // Nextmine: mark-received-from-purchased fjernet - køber kan først
        // bekræfte modtagelse fra DELIVERED (efter sælger har markeret afsendt).
        [transitions.AUTO_CANCEL]: states.CANCELED,
        [transitions.CANCEL]: states.CANCELED,
      },
    },

    [states.CANCELED]: {},

    [states.DELIVERED]: {
      on: {
        [transitions.MARK_RECEIVED]: states.RECEIVED,
        // Nextmine: passiv køber → 20 dage → direkte til completed (udbetaling)
        [transitions.AUTO_MARK_RECEIVED]: states.COMPLETED,
        [transitions.DISPUTE]: states.DISPUTED,
        [transitions.OPERATOR_DISPUTE]: states.DISPUTED,
      },
    },

    [states.DISPUTED]: {
      on: {
        [transitions.AUTO_CANCEL_FROM_DISPUTED]: states.CANCELED,
        [transitions.CANCEL_FROM_DISPUTED]: states.CANCELED,
        // Nextmine: operatør løser tvist → direkte til completed (udbetaling)
        [transitions.MARK_RECEIVED_FROM_DISPUTED]: states.COMPLETED,
        // Nextmine: operatør beslutter køber skal returnere mod refusion
        [transitions.REQUEST_RETURN]: states.RETURN_REQUESTED,
      },
    },

    // Nextmine: køber skal sende varen retur og indtaste trackingnummer
    [states.RETURN_REQUESTED]: {
      on: {
        [transitions.SUBMIT_RETURN_TRACKING]: states.RETURN_SHIPPED,
        // Køber returnerer aldrig → salget gennemføres, sælger udbetales
        [transitions.EXPIRE_RETURN_REQUEST]: states.COMPLETED,
      },
    },
    // Nextmine: returen er på vej - sælger bekræfter modtaget (eller flagger fejl)
    [states.RETURN_SHIPPED]: {
      on: {
        [transitions.CONFIRM_RETURN_RECEIVED]: states.CANCELED,
        [transitions.OPERATOR_CONFIRM_RETURN]: states.CANCELED,
        // Returen kom beskadiget/forkert → tilbage til Nextmine-gennemgang
        [transitions.RETURN_NOT_AS_EXPECTED]: states.DISPUTED,
        // Sælger bekræfter aldrig → køber auto-refunderes
        [transitions.AUTO_REFUND_RETURN]: states.CANCELED,
      },
    },

    [states.RECEIVED]: {
      on: {
        // Nextmine: 14-dages-hold → udbetaling; eller operatør refunderer (stop)
        [transitions.AUTO_COMPLETE]: states.COMPLETED,
        [transitions.OPERATOR_REFUND_DURING_HOLD]: states.CANCELED,
      },
    },

    [states.COMPLETED]: {
      on: {
        [transitions.EXPIRE_REVIEW_PERIOD]: states.REVIEWED,
        [transitions.REVIEW_1_BY_CUSTOMER]: states.REVIEWED_BY_CUSTOMER,
        [transitions.REVIEW_1_BY_PROVIDER]: states.REVIEWED_BY_PROVIDER,
      },
    },

    [states.REVIEWED_BY_CUSTOMER]: {
      on: {
        [transitions.REVIEW_2_BY_PROVIDER]: states.REVIEWED,
        [transitions.EXPIRE_PROVIDER_REVIEW_PERIOD]: states.REVIEWED,
      },
    },
    [states.REVIEWED_BY_PROVIDER]: {
      on: {
        [transitions.REVIEW_2_BY_CUSTOMER]: states.REVIEWED,
        [transitions.EXPIRE_CUSTOMER_REVIEW_PERIOD]: states.REVIEWED,
      },
    },
    [states.REVIEWED]: { type: 'final' },
  },
};

// Check if a transition is the kind that should be rendered
// when showing transition history (e.g. ActivityFeed)
// The first transition and most of the expiration transitions made by system are not relevant
export const isRelevantPastTransition = transition => {
  return [
    transitions.CONFIRM_PAYMENT,
    // Nextmine: bud/modbud-trin vises i aktivitets-tidslinjen
    transitions.MAKE_OFFER,
    transitions.PROVIDER_ACCEPT_OFFER,
    transitions.PROVIDER_DECLINE_OFFER,
    transitions.CUSTOMER_WITHDRAW_OFFER,
    transitions.PROVIDER_MAKE_COUNTER_OFFER,
    transitions.CUSTOMER_ACCEPT_COUNTER,
    transitions.CUSTOMER_DECLINE_COUNTER,
    transitions.CUSTOMER_MAKE_COUNTER_OFFER,
    // Nextmine: fragt-quote-trin vises i aktivitets-tidslinjen
    transitions.REQUEST_SHIPPING_QUOTE,
    transitions.PROVIDER_PROVIDE_SHIPPING_QUOTE,
    transitions.PROVIDER_DECLINE_SHIPPING_REQUEST,
    transitions.CUSTOMER_WITHDRAW_SHIPPING_REQUEST,
    transitions.CUSTOMER_DECLINE_SHIPPING_QUOTE,
    transitions.MAKE_OFFER_AFTER_SHIPPING_QUOTE,
    transitions.AUTO_CANCEL,
    transitions.CANCEL,
    transitions.MARK_DELIVERED,
    transitions.OPERATOR_MARK_DELIVERED,
    transitions.DISPUTE,
    transitions.OPERATOR_DISPUTE,
    transitions.AUTO_COMPLETE,
    transitions.AUTO_CANCEL_FROM_DISPUTED,
    transitions.CANCEL_FROM_DISPUTED,
    // Nextmine: retur-trin vises i aktivitets-tidslinjen
    transitions.REQUEST_RETURN,
    transitions.SUBMIT_RETURN_TRACKING,
    transitions.CONFIRM_RETURN_RECEIVED,
    transitions.OPERATOR_CONFIRM_RETURN,
    transitions.RETURN_NOT_AS_EXPECTED,
    transitions.AUTO_REFUND_RETURN,
    transitions.EXPIRE_RETURN_REQUEST,
    transitions.REVIEW_1_BY_CUSTOMER,
    transitions.REVIEW_1_BY_PROVIDER,
    transitions.REVIEW_2_BY_CUSTOMER,
    transitions.REVIEW_2_BY_PROVIDER,
  ].includes(transition);
};
export const isCustomerReview = transition => {
  return [transitions.REVIEW_1_BY_CUSTOMER, transitions.REVIEW_2_BY_CUSTOMER].includes(transition);
};

export const isProviderReview = transition => {
  return [transitions.REVIEW_1_BY_PROVIDER, transitions.REVIEW_2_BY_PROVIDER].includes(transition);
};

// Check if the given transition is privileged.
//
// Privileged transitions need to be handled from a secure context,
// i.e. the backend. This helper is used to check if the transition
// should go through the local API endpoints, or if using JS SDK is
// enough.
export const isPrivileged = transition => {
  return [
    transitions.REQUEST_PAYMENT,
    transitions.REQUEST_PAYMENT_AFTER_INQUIRY,
    // Nextmine: bud/modbud sætter custom line-items (pris) → skal være privileged
    transitions.MAKE_OFFER,
    transitions.PROVIDER_MAKE_COUNTER_OFFER,
    transitions.CUSTOMER_MAKE_COUNTER_OFFER,
    transitions.REQUEST_PAYMENT_AFTER_OFFER,
    // Nextmine: fragt-quote sætter custom line-items (fragt) → privileged
    transitions.PROVIDER_PROVIDE_SHIPPING_QUOTE,
    transitions.REQUEST_PAYMENT_AFTER_SHIPPING_QUOTE,
    transitions.MAKE_OFFER_AFTER_SHIPPING_QUOTE,
  ].includes(transition);
};

// Check when transaction is completed (item is received and review notifications sent)
export const isCompleted = transition => {
  const txCompletedTransitions = [
    transitions.AUTO_COMPLETE,
    transitions.REVIEW_1_BY_CUSTOMER,
    transitions.REVIEW_1_BY_PROVIDER,
    transitions.REVIEW_2_BY_CUSTOMER,
    transitions.REVIEW_2_BY_PROVIDER,
    transitions.EXPIRE_REVIEW_PERIOD,
    transitions.EXPIRE_CUSTOMER_REVIEW_PERIOD,
    transitions.EXPIRE_PROVIDER_REVIEW_PERIOD,
  ];
  return txCompletedTransitions.includes(transition);
};

// Check when transaction is refunded (order did not happen)
// In these transitions action/stripe-refund-payment is called
export const isRefunded = transition => {
  const txRefundedTransitions = [
    transitions.EXPIRE_PAYMENT,
    transitions.CANCEL,
    transitions.AUTO_CANCEL,
    transitions.AUTO_CANCEL_FROM_DISPUTED,
    transitions.CANCEL_FROM_DISPUTED,
    // Nextmine: retur bekræftet → køber refunderet
    transitions.CONFIRM_RETURN_RECEIVED,
    transitions.OPERATOR_CONFIRM_RETURN,
    transitions.AUTO_REFUND_RETURN,
  ];
  return txRefundedTransitions.includes(transition);
};

// Nextmine: sælger skal også reagere på indkomne bud (offer-pending) og
// fragt-forespørgsler (shipping-quote-requested)
export const statesNeedingProviderAttention = [
  states.PURCHASED,
  states.OFFER_PENDING,
  states.SHIPPING_QUOTE_REQUESTED,
  // Nextmine: sælger skal bekræfte modtaget retur
  states.RETURN_SHIPPED,
];

// Nextmine: køber skal handle for at få sin refusion - send varen retur + indtast
// trackingnummer. Giver et notifikations-badge i indbakken.
export const statesNeedingCustomerAttention = [states.RETURN_REQUESTED];
